<template>
    <div class="c-input-wrapper" :class="{ 'inline-input': inline }">
        <label v-if="label" class="c-input-label" :class="labelClass" :for="id">
            {{ label }}<template v-if="required">*</template>
        </label>
        <div class="w-100" :class="{ 'input-group': !!slot.append || !!slot.prepend }">
            <div v-if="slot.prepend" class="input-group-prepend border">
                <slot name="prepend" />
            </div>
            <input
                :id="id"
                v-model="model"
                class="form-control"
                :class="computedClass"
                :type="computedType"
                :disabled="disabled"
                :placeholder="placeholder"
                :required="required"
                :minlength="minlength"
                :maxlength="maxlength"
                :step="step"
                :min="min"
                :max="max"
                @blur="emit('blur', model)"
                @keyup.enter="emit('enter', model)"
                @input="triggerDebounce"
            />
            <div v-if="slot.append" class="input-group-append border">
                <slot name="append" />
            </div>
        </div>
        <span v-if="hint?.length" class="input-hint">{{ hint }}</span>
        <c-error v-if="error" :message="errorMsg" />
    </div>
</template>

<script setup lang="ts">
import { HtmlHTMLAttributes, InputHTMLAttributes, computed, useSlots } from 'vue';
import { debounce } from 'lodash';
import CError from '@/components/shared/CError.vue';

type InputValue = string | number;
type InputSize = 'lg' | 'md' | 'sm' | 'xs';
type InputType = 'text' | 'search' | 'number' | 'decimal' | 'email';

const props = withDefaults(
    defineProps<{
        value?: InputValue;
        id?: InputHTMLAttributes['id'];
        inputClass?: InputHTMLAttributes['class'];
        type?: InputType;
        disabled?: boolean;
        required?: boolean;
        minlength?: InputHTMLAttributes['minlength'];
        maxlength?: InputHTMLAttributes['maxlength'];
        min?: InputHTMLAttributes['min'];
        max?: InputHTMLAttributes['max'];
        step?: InputHTMLAttributes['step'];
        fractionDigits?: number;
        placeholder?: HtmlHTMLAttributes['placeholder'];
        label?: string;
        labelClass?: HtmlHTMLAttributes['class'];
        inline?: boolean;
        inputSize?: InputSize;
        error?: boolean;
        errorMsg?: string;
        debounce?: number;
        hint?: string;
    }>(),
    {
        type: 'search',
        inputSize: 'md',
        debounce: 1000,
        fractionDigits: 2
    }
);

const emit = defineEmits<{
    (event: 'input', value: InputValue): void;
    (event: 'blur', value: InputValue): void;
    (event: 'enter', value: InputValue): void;
    (event: 'debounce', value: InputValue): void;
}>();

const slot = useSlots();

const computedClass = computed(() => {
    const classes = [props.inputClass, `form-control-${props.inputSize}`];

    if (props.error) {
        classes.push('is-invalid');
    }

    return classes;
});

const computedType = computed(() => {
    const proxyTypes: Record<string, string> = {
        decimal: 'number'
    };

    return proxyTypes[props.type] || props.type;
});

const model = computed({
    get: () => props.value || '',
    set: (value?: InputValue) => {
        emit('input', validateInputValue(value));
    }
});

const validateInputValue = (value?: InputValue): InputValue => {
    let newVal = value;

    if (!['number', 'decimal'].includes(props.type)) return newVal || '';

    if (value == undefined && !props.min) return newVal || 0;

    newVal = Number(value);

    if (props.type == 'decimal') {
        const decimal = newVal.toFixed(props.fractionDigits);
        newVal = parseFloat(decimal);
    }

    return newVal;
};

const triggerDebounce = debounce(() => {
    emit('debounce', model.value);
}, props.debounce);
</script>

<style lang="scss" scoped>
@import '@/assets/css/utilities/functions';

.c-input-wrapper {
    .input-hint {
        display: block;
        font-size: 0.625rem;
    }
    .c-input-label {
        font-size: 0.875rem;
        margin-bottom: 0.19rem;
    }

    &.inline-input {
        display: flex;
        align-items: center;
        .c-input-label {
            margin-bottom: 0;
            margin-right: 0.25rem;
        }
    }

    input {
        border-color: get-color('gray-light');
        box-shadow: none;
        &::placeholder {
            color: get-color('gray');
        }
    }

    .input-group {
        &:has(.input-group-append) .form-control {
            border-right: unset;
        }

        .input-group-append {
            border-top-right-radius: 0.25rem;
            border-bottom-right-radius: 0.25rem;
        }

        &:has(.input-group-prepend) .form-control {
            border-left: unset;
        }

        .input-group-prepend {
            border-top-left-radius: 0.25rem;
            border-bottom-left-radius: 0.25rem;
        }
    }
}
</style>
